"use client";

import React, { useEffect } from "react";
import SEO from "@/components/Common/SEO/Index";
import Locale from "@/components/Common/Locale/Index";
import Header from "@/components/Common/Header/Index";
import dynamic from "next/dynamic";
// 仅桌面端渲染、数据自己取；静态引入会把 sanitize-html 链钉进 _app（-100 kB）
const Footer = dynamic(() => import("@/components/Common/Footer/Index"), { ssr: false });
import SideBar from "@/components/Common/SideBar/Index";
import { ConfigProvider } from "antd";
import theme from "@/antd.config";
import { useRouter } from "next/router";
import zhCN from "antd/locale/zh_CN";
import zhHK from "antd/locale/zh_HK";
import enUS from "antd/locale/en_US";
import koKr from "antd/locale/ko_KR";
import { AppProgressBar as ProgressBar } from "next-nprogress-bar";
import RedirectSignIn from "@/components/Common/RedirectSignIn";
import { setCountryCode, setFonts, setIsMobile, setIsSmallScreen } from "@/store/app";
import { setProductFilterVisible, setProductAttrSelectVisible } from "@/store/mobilePopup";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/index";
import debounce from "lodash/debounce";
import defaultSettings from "@/config/settings";
import Zendesk from "@/components/Common/Zendesk/Index";

export default function Layout({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const currentLocale: string | undefined = router.locale || "en";
  const dispatch = useAppDispatch();
  const appStore = useAppSelector((state: RootState) => state.app);
  const { isMobile } = appStore;
  const { mobileWidth, smallScreenWidth } = defaultSettings;

  const antdLocales = {
    sc: zhCN,
    tc: zhHK,
    en: enUS,
    kr: koKr,
  };

  /**
   * set fonts by locale
   */
  const handleSetFonts = async () => {
    if (
      currentLocale === "sc" ||
      currentLocale === "tc" ||
      currentLocale === "jp" ||
      currentLocale === "vn" ||
      currentLocale === "ar"
    ) {
      document.body.className = "font-" + currentLocale;
      dispatch(setFonts("font-" + currentLocale));
    } else {
      document.body.className = "font-en";
      dispatch(setFonts("font-en"));
    }
  };

  /**
   * set is or not mobile device
   */
  const handleSetIsMobile = () => {
    const clientWidth = document.body.clientWidth;
    // is mobile device, when client width less than 1024px
    dispatch(setIsMobile(clientWidth < mobileWidth));
    // is small screen device, when client width less than 768px
    dispatch(setIsSmallScreen(clientWidth < smallScreenWidth));
    // pc device, hidden mobile popup
    if (clientWidth >= mobileWidth) {
      dispatch(setProductFilterVisible(false));
      dispatch(setProductAttrSelectVisible(false));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchCountryCode = async () => {
      try {
        const resp = await fetch(process.env.NEXT_PUBLIC_IP_DETECTION_API + "/");
        if (!resp.ok) throw new Error("ip detect failed");
        const data = await resp.json();
        const country_code = data?.code === "0" && data?.data?.iso_country_code ? data.data.iso_country_code : "CN";
        if (!cancelled) {
          dispatch(setCountryCode(country_code));
        }
      } catch (e) {
        if (!cancelled) {
          dispatch(setCountryCode("CN"));
        }
        console.error("Failed to detect country code:", e);
      }
    };
    fetchCountryCode();
    return () => {
      cancelled = true;
    };
  }, [currentLocale, dispatch]);

  useEffect(() => {
    handleSetFonts();
    handleSetIsMobile();

    // use debounce
    const debouncedResizeHandler = debounce(handleSetIsMobile, 500);
    window.addEventListener("resize", debouncedResizeHandler);

    return () => {
      window.removeEventListener("resize", debouncedResizeHandler);
      debouncedResizeHandler.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocale]);


  return (
    <ConfigProvider theme={theme} locale={antdLocales[currentLocale] || enUS}>
      <RedirectSignIn />
      <div className={`flex w-full flex-col text-sm text-stone-500`}>
        <SEO title="" />
        <Locale />
        <Header />
        <main className="flex flex-col items-center">{children}</main>
        {!isMobile && (
          <div className="min-h-[400px] w-full" id="pageFooter">
            <Footer />
          </div>
        )}
        <SideBar />
        <Zendesk />
        <ProgressBar height="2px" color="#d91d22" options={{ showSpinner: false }} shallowRouting />
      </div>
    </ConfigProvider>
  );
}
